import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import CompanyAdminSidebar from '@/components/CompanyAdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Users, 
  UserPlus, 
  Trash2,
  Mail,
  Calendar,
  Copy,
  Link2,
  CheckCircle,
  Clock as ClockIcon,
  Shield,
  ShieldCheck,
  RefreshCw,
  CreditCard,
  Pencil,
  Save,
  X,
  Archive,
  ArchiveRestore,
  UserX,
  MapPin,
  Briefcase,
  CalendarDays,
  Home,
  AlertTriangle
} from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmployeeManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [archivedEmployees, setArchivedEmployees] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [hardDeleteDialogOpen, setHardDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteType, setDeleteType] = useState('employee');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newInvite, setNewInvite] = useState({ name: '', email: '', position: '', payroll_id: '', date_of_birth: '', address: '' });
  const [copiedLink, setCopiedLink] = useState(null);
  const [resendingInvite, setResendingInvite] = useState(null);
  const [editingPayrollId, setEditingPayrollId] = useState(null);
  const [payrollIdValue, setPayrollIdValue] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editForm, setEditForm] = useState({ 
    name: '', 
    email: '', 
    payroll_id: '',
    position: '',
    date_of_birth: '',
    address: ''
  });
  const [editSaving, setEditSaving] = useState(false);
  const [togglingOffsite, setTogglingOffsite] = useState(null);
  const [offsiteDialogOpen, setOffsiteDialogOpen] = useState(false);
  const [selectedOffsiteEmployee, setSelectedOffsiteEmployee] = useState(null);
  const [billingEstimate, setBillingEstimate] = useState(null);
  const [offsiteSettings, setOffsiteSettings] = useState({
    allow_offsite_clock: false,
    offsite_enforcement_mode: 'block'
  });
  const [savingOffsite, setSavingOffsite] = useState(false);
  const [togglingRoster, setTogglingRoster] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [empResponse, archivedResponse, invResponse] = await Promise.all([
        axios.get(`${API}/employees?include_admin=true`),
        axios.get(`${API}/employees/archived`),
        axios.get(`${API}/employees/invites`)
      ]);
      setEmployees(empResponse.data);
      setArchivedEmployees(archivedResponse.data);
      setInvites(invResponse.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInviteEmployee = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/employees/invite`, {
        name: newInvite.name,
        email: newInvite.email,
        position: newInvite.position || null,
        payroll_id: newInvite.payroll_id || null,
        date_of_birth: newInvite.date_of_birth || null,
        address: newInvite.address || null
      });
      setInvites(prev => [...prev, response.data]);
      setInviteDialogOpen(false);
      setNewInvite({ name: '', email: '', position: '', payroll_id: '', date_of_birth: '', address: '' });
      setBillingEstimate(null);
      toast.success('Invite created! Copy the link and share it with the employee.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invite');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchBillingEstimate = async () => {
    try {
      const res = await axios.get(`${API}/billing/estimate-add-one`);
      setBillingEstimate(res.data);
    } catch (error) {
      // Silently fail - billing estimate is optional
      console.log('Billing estimate not available');
    }
  };

  const openInviteDialog = () => {
    setInviteDialogOpen(true);
    fetchBillingEstimate();
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    setIsSubmitting(true);
    try {
      if (deleteType === 'employee') {
        await axios.delete(`${API}/employees/${selectedItem.id}`);
        setEmployees(prev => prev.filter(e => e.id !== selectedItem.id));
        toast.success('Employee removed successfully!');
      } else {
        await axios.delete(`${API}/employees/invites/${selectedItem.id}`);
        setInvites(prev => prev.filter(i => i.id !== selectedItem.id));
        toast.success('Invite cancelled successfully!');
      }
      setDeleteDialogOpen(false);
      setSelectedItem(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleArchive = async () => {
    if (!selectedItem) return;
    setIsSubmitting(true);
    try {
      const response = await axios.put(`${API}/employees/${selectedItem.id}/archive`);
      toast.success(response.data.message);
      setArchiveDialogOpen(false);
      setSelectedItem(null);
      fetchData(); // Refresh all lists
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive employee');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleHardDelete = async () => {
    if (!selectedItem) return;
    setIsSubmitting(true);
    try {
      const response = await axios.delete(`${API}/employees/${selectedItem.id}/hard-delete?confirm=true`);
      toast.success(response.data.message);
      setHardDeleteDialogOpen(false);
      setSelectedItem(null);
      fetchData(); // Refresh all lists
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete employee permanently');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRestore = async (employee) => {
    try {
      const response = await axios.put(`${API}/employees/${employee.id}/archive`);
      toast.success(response.data.message);
      fetchData(); // Refresh all lists
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore employee');
    }
  };

  const toggleManager = async (employee) => {
    try {
      const response = await axios.put(`${API}/employees/${employee.id}/manager`);
      setEmployees(prev => prev.map(e => 
        e.id === employee.id ? { ...e, is_manager: response.data.is_manager } : e
      ));
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update manager status');
    }
  };

  const copyInviteLink = async (invite) => {
    try {
      await navigator.clipboard.writeText(invite.invite_link);
      setCopiedLink(invite.id);
      toast.success('Invite link copied to clipboard!');
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const resendInviteEmail = async (invite) => {
    setResendingInvite(invite.id);
    try {
      await axios.post(`${API}/employees/invites/${invite.id}/resend`);
      toast.success(`Invitation email resent to ${invite.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend invitation email');
    } finally {
      setResendingInvite(null);
    }
  };

  const savePayrollId = async (employeeId) => {
    try {
      await axios.put(`${API}/employees/${employeeId}/payroll-id`, {
        payroll_id: payrollIdValue
      });
      toast.success('Payroll ID saved');
      setEditingPayrollId(null);
      fetchData(); // Refresh employee list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save payroll ID');
    }
  };

  const startEditPayrollId = (employee) => {
    setEditingPayrollId(employee.id);
    setPayrollIdValue(employee.payroll_id || '');
  };

  const openEditDialog = (employee) => {
    setEditingEmployee(employee);
    setEditForm({
      name: employee.name || '',
      email: employee.email || '',
      payroll_id: employee.payroll_id || '',
      position: employee.position || '',
      date_of_birth: employee.date_of_birth || '',
      address: employee.address || '',
      scheduling_role: employee.scheduling_role || 'normal',
      permitted_start_times: (employee.permitted_start_times || []).join(', ')
    });
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!editingEmployee) return;
    
    if (!editForm.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!editForm.email.trim()) {
      toast.error('Email is required');
      return;
    }

    setEditSaving(true);
    try {
      await axios.put(`${API}/employees/${editingEmployee.id}`, {
        name: editForm.name.trim(),
        email: editForm.email.trim().toLowerCase(),
        payroll_id: editForm.payroll_id.trim() || null,
        position: editForm.position.trim() || null,
        date_of_birth: editForm.date_of_birth || null,
        address: editForm.address.trim() || null
      });

      // Save scheduling role separately (dedicated AI-roster endpoint)
      try {
        const permitted = (editForm.permitted_start_times || '')
          .split(',').map((s) => s.trim()).filter(Boolean);
        await axios.put(`${API}/api/roster/ai/employees/${editingEmployee.id}/scheduling-role`, {
          scheduling_role: editForm.scheduling_role === 'normal' ? null : editForm.scheduling_role,
          permitted_start_times: permitted,
        });
      } catch (schedErr) {
        // Not fatal — surface a warning
        console.warn('Could not save scheduling role:', schedErr?.response?.data?.detail);
      }

      toast.success('Employee updated successfully');
      setEditDialogOpen(false);
      setEditingEmployee(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update employee');
    } finally {
      setEditSaving(false);
    }
  };

  const handleToggleOffsite = async (employee) => {
    setTogglingOffsite(employee.id);
    try {
      const newValue = !employee.allow_offsite_clock;
      const response = await axios.put(`${API}/employees/${employee.id}/offsite-toggle`, {
        allow_offsite_clock: newValue,
        offsite_enforcement_mode: employee.offsite_enforcement_mode || 'block'
      });
      setEmployees(prev => prev.map(e => 
        e.id === employee.id ? { 
          ...e, 
          allow_offsite_clock: newValue,
          offsite_enforcement_mode: response.data.offsite_enforcement_mode 
        } : e
      ));
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update off-site setting');
    } finally {
      setTogglingOffsite(null);
    }
  };

  const openOffsiteDialog = (employee) => {
    setSelectedOffsiteEmployee(employee);
    setOffsiteSettings({
      allow_offsite_clock: employee.allow_offsite_clock || false,
      offsite_enforcement_mode: employee.offsite_enforcement_mode || 'block'
    });
    setOffsiteDialogOpen(true);
  };

  const handleSaveOffsiteSettings = async () => {
    if (!selectedOffsiteEmployee) return;
    setSavingOffsite(true);
    try {
      const response = await axios.put(`${API}/employees/${selectedOffsiteEmployee.id}/offsite-toggle`, offsiteSettings);
      setEmployees(prev => prev.map(e => 
        e.id === selectedOffsiteEmployee.id ? { 
          ...e, 
          allow_offsite_clock: offsiteSettings.allow_offsite_clock,
          offsite_enforcement_mode: offsiteSettings.offsite_enforcement_mode
        } : e
      ));
      toast.success(response.data.message);
      setOffsiteDialogOpen(false);
      setSelectedOffsiteEmployee(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update off-site settings');
    } finally {
      setSavingOffsite(false);
    }
  };

  const handleToggleRoster = async (employee) => {
    setTogglingRoster(employee.id);
    try {
      const newValue = employee.on_roster === false ? true : false;
      const response = await axios.put(`${API}/employees/${employee.id}/roster-toggle`, {
        on_roster: newValue
      });
      setEmployees(prev => prev.map(e => 
        e.id === employee.id ? { ...e, on_roster: newValue } : e
      ));
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update roster setting');
    } finally {
      setTogglingRoster(null);
    }
  };

  const pendingInvites = invites.filter(i => i.status === 'pending');
  const adminUser = employees.find(e => e.role === 'company_admin');
  const managers = employees.filter(e => (e.is_manager || e.role === 'manager') && e.role !== 'company_admin');
  const regularEmployees = employees.filter(e => !e.is_manager && e.role !== 'manager' && e.role !== 'company_admin');

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-slate-50 w-full overflow-x-hidden">
      <CompanyAdminSidebar />
      
      <main className="flex-1 min-w-0 p-3 sm:p-4 md:p-8 pb-20 md:pb-8 overflow-x-hidden w-full">
        <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold font-['Manrope'] tracking-tight">Employees</h1>
              <p className="text-sm sm:text-base text-slate-500">Manage your team members and invitations</p>
            </div>
            <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="gap-2 bg-[hsl(243,65%,20%)] hover:bg-[hsl(243,65%,25%)]"
                  data-testid="invite-employee-btn"
                  onClick={openInviteDialog}
                >
                  <UserPlus className="w-4 h-4" />
                  Invite Employee
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-['Manrope']">Invite New Employee</DialogTitle>
                  <DialogDescription>
                    Create an invitation for a new employee. You'll receive a link to share with them.
                  </DialogDescription>
                </DialogHeader>
                {billingEstimate?.will_increase && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-2">
                    <p className="text-amber-800 text-sm font-medium flex items-center gap-2">
                      <CreditCard className="w-4 h-4" />
                      Billing Notice
                    </p>
                    <p className="text-amber-700 text-sm mt-1">
                      {billingEstimate.message}
                    </p>
                  </div>
                )}
                <form onSubmit={handleInviteEmployee} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="emp-name">Full Name</Label>
                    <Input
                      id="emp-name"
                      placeholder="John Doe"
                      value={newInvite.name}
                      onChange={(e) => setNewInvite({ ...newInvite, name: e.target.value })}
                      required
                      data-testid="invite-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emp-email">Email</Label>
                    <Input
                      id="emp-email"
                      type="email"
                      placeholder="john@company.com"
                      value={newInvite.email}
                      onChange={(e) => setNewInvite({ ...newInvite, email: e.target.value })}
                      required
                      data-testid="invite-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emp-position">Position (Optional)</Label>
                    <Input
                      id="emp-position"
                      placeholder="e.g., Barista, Manager, Chef"
                      value={newInvite.position}
                      onChange={(e) => setNewInvite({ ...newInvite, position: e.target.value })}
                      data-testid="invite-position-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emp-payroll-id">Payroll ID (Optional)</Label>
                    <Input
                      id="emp-payroll-id"
                      placeholder="e.g., EMP001"
                      value={newInvite.payroll_id}
                      onChange={(e) => setNewInvite({ ...newInvite, payroll_id: e.target.value })}
                      data-testid="invite-payroll-id-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emp-dob">Date of Birth (Optional)</Label>
                    <Input
                      id="emp-dob"
                      type="date"
                      value={newInvite.date_of_birth}
                      onChange={(e) => setNewInvite({ ...newInvite, date_of_birth: e.target.value })}
                      data-testid="invite-dob-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emp-address">Address (Optional)</Label>
                    <Input
                      id="emp-address"
                      placeholder="e.g., 123 Main St, Auckland"
                      value={newInvite.address}
                      onChange={(e) => setNewInvite({ ...newInvite, address: e.target.value })}
                      data-testid="invite-address-input"
                    />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setInviteDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      className="bg-[hsl(243,65%,20%)] hover:bg-[hsl(243,65%,25%)]"
                      disabled={isSubmitting}
                      data-testid="submit-invite-btn"
                    >
                      {isSubmitting ? <div className="spinner" /> : 'Create Invite'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="employees" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="employees" data-testid="employees-tab">
                Employees ({employees.length})
              </TabsTrigger>
              <TabsTrigger value="invites" data-testid="invites-tab">
                Pending Invites ({pendingInvites.length})
              </TabsTrigger>
              <TabsTrigger value="archived" data-testid="archived-tab">
                Archived ({archivedEmployees.length})
              </TabsTrigger>
            </TabsList>

            {/* Employees Tab */}
            <TabsContent value="employees">
              <Card className="border-0 shadow-lg">
                <CardHeader className="border-b">
                  <CardTitle className="font-['Manrope'] flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Team Members
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {loading ? (
                    <div className="flex justify-center py-12">
                      <div className="spinner" />
                    </div>
                  ) : employees.length === 0 ? (
                    <div className="empty-state py-12">
                      <Users className="empty-state-icon" />
                      <p className="text-slate-500">No employees yet</p>
                      <p className="text-sm text-slate-400">Invite your first team member to get started</p>
                    </div>
                  ) : (
                    <ScrollArea className="h-[400px]">
                      <div className="divide-y">
                        {/* Admin Section */}
                        {adminUser && (
                          <>
                            <div className="px-4 py-2 bg-purple-50 border-b border-purple-100">
                              <p className="text-xs font-semibold text-purple-600 uppercase tracking-wider flex items-center gap-1">
                                <Shield className="w-3 h-3" />
                                Company Admin
                              </p>
                            </div>
                            <div 
                              className="flex flex-col sm:flex-row sm:items-center justify-between px-3 sm:px-6 py-3 sm:py-4 hover:bg-slate-50 transition-colors bg-purple-50/30 gap-2 sm:gap-4"
                              data-testid={`employee-row-${adminUser.id}`}
                            >
                              <div className="flex items-center gap-3 sm:gap-4">
                                <div className="employee-avatar bg-purple-600 shrink-0">
                                  {adminUser.name.charAt(0).toUpperCase()}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                                    <p className="font-medium text-sm sm:text-base truncate">{adminUser.name}</p>
                                    <Badge className="bg-purple-100 text-purple-700 text-[10px] shrink-0">
                                      <Shield className="w-3 h-3 mr-0.5 sm:mr-1" />
                                      Admin
                                    </Badge>
                                  </div>
                                  <p className="text-xs sm:text-sm text-slate-500 truncate">{adminUser.email}</p>
                                  <div className="flex items-center gap-2 sm:gap-4 text-xs text-slate-400 mt-1 flex-wrap">
                                    {adminUser.position && (
                                      <span className="flex items-center gap-1">
                                        <Briefcase className="w-2.5 h-2.5" />
                                        {adminUser.position}
                                      </span>
                                    )}
                                    <span className="hidden sm:flex items-center gap-1">
                                      <Calendar className="w-3 h-3" />
                                      Joined {format(new Date(adminUser.created_at), 'dd/MM/yyyy')}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              {/* Edit button for admin profile */}
                              <div className="flex items-center gap-2 self-end sm:self-auto">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setEditingEmployee(adminUser);
                                    setEditForm({
                                      name: adminUser.name,
                                      email: adminUser.email,
                                      payroll_id: adminUser.payroll_id || '',
                                      position: adminUser.position || '',
                                      date_of_birth: adminUser.date_of_birth || '',
                                      address: adminUser.address || ''
                                    });
                                    setEditDialogOpen(true);
                                  }}
                                  className="text-purple-600 hover:text-purple-700 hover:bg-purple-100"
                                  data-testid={`edit-admin-btn-${adminUser.id}`}
                                >
                                  <Pencil className="w-4 h-4 mr-1" />
                                  Edit
                                </Button>
                              </div>
                            </div>
                          </>
                        )}

                        {/* Managers Section - Always show header */}
                        <div className="px-4 py-2 bg-indigo-50 border-b border-indigo-100">
                          <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider flex items-center gap-1">
                            <ShieldCheck className="w-3 h-3" />
                            Managers ({managers.length})
                          </p>
                        </div>
                        {managers.length === 0 ? (
                          <div className="px-6 py-4 text-center text-slate-400 text-sm bg-indigo-50/30">
                            No managers assigned
                          </div>
                        ) : (
                          managers.map((employee) => (
                          <div 
                            key={employee.id} 
                            className="flex flex-col sm:flex-row sm:items-center justify-between px-3 sm:px-6 py-3 sm:py-4 hover:bg-slate-50 transition-colors bg-indigo-50/30 gap-2 sm:gap-4"
                            data-testid={`employee-row-${employee.id}`}
                          >
                            <div className="flex items-center gap-3 sm:gap-4">
                              <div className="employee-avatar bg-indigo-600 shrink-0">
                                {employee.name.charAt(0).toUpperCase()}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                                  <p className="font-medium text-sm sm:text-base truncate">{employee.name}</p>
                                  <Badge className="bg-indigo-100 text-indigo-700 text-[10px] shrink-0">
                                    <ShieldCheck className="w-3 h-3 mr-0.5 sm:mr-1" />
                                    Manager
                                  </Badge>
                                </div>
                                <p className="text-xs sm:text-sm text-slate-500 truncate">{employee.email}</p>
                                <div className="flex items-center gap-2 sm:gap-4 text-xs text-slate-400 mt-1 flex-wrap">
                                  {employee.position && (
                                    <span className="flex items-center gap-1">
                                      <Briefcase className="w-2.5 h-2.5" />
                                      {employee.position}
                                    </span>
                                  )}
                                  {employee.allow_offsite_clock && (
                                    <span className="flex items-center gap-1 text-green-600">
                                      <MapPin className="w-2.5 h-2.5" />
                                      Off-site
                                    </span>
                                  )}
                                  <span className="hidden sm:flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    Joined {format(new Date(employee.created_at), 'dd/MM/yyyy')}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 sm:gap-2 flex-wrap self-end sm:self-auto">
                              {/* Roster Toggle */}
                              <Button
                                variant="outline"
                                size="sm"
                                className={`text-xs sm:text-sm ${employee.on_roster !== false
                                  ? 'text-blue-600 border-blue-200 bg-blue-50 hover:bg-blue-100' 
                                  : 'text-slate-400 border-slate-200 hover:text-slate-600'}`}
                                onClick={() => handleToggleRoster(employee)}
                                disabled={togglingRoster === employee.id}
                                data-testid={`roster-toggle-manager-${employee.id}`}
                              >
                                <Calendar className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                                <span className="hidden sm:inline">{employee.on_roster !== false ? 'On Roster' : 'Off Roster'}</span>
                              </Button>
                              {/* Off-site Toggle with Config */}
                              <Button
                                variant="outline"
                                size="sm"
                                className={`text-xs sm:text-sm ${employee.allow_offsite_clock 
                                  ? 'text-green-600 border-green-200 bg-green-50 hover:bg-green-100' 
                                  : 'text-slate-500 hover:text-slate-700'}`}
                                onClick={() => openOffsiteDialog(employee)}
                                data-testid={`offsite-config-${employee.id}`}
                              >
                                <MapPin className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                                <span className="hidden sm:inline">{employee.allow_offsite_clock 
                                  ? `Off-site` 
                                  : 'Off-site'}</span>
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs sm:text-sm text-slate-600 hover:text-blue-600 hover:border-blue-200"
                                onClick={() => openEditDialog(employee)}
                                data-testid={`edit-employee-${employee.id}`}
                              >
                                <Pencil className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                                <span className="hidden sm:inline">Edit</span>
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs sm:text-sm text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                                onClick={() => toggleManager(employee)}
                                data-testid={`toggle-manager-${employee.id}`}
                                title="Remove Manager"
                              >
                                <Shield className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                                <span className="hidden sm:inline">Remove Manager</span>
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs sm:text-sm text-amber-600 hover:text-amber-700 hover:border-amber-300 hover:bg-amber-50"
                                onClick={() => {
                                  setSelectedItem(employee);
                                  setArchiveDialogOpen(true);
                                }}
                                data-testid={`archive-employee-${employee.id}`}
                              >
                                <Archive className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                                <span className="hidden sm:inline">Archive</span>
                              </Button>
                            </div>
                          </div>
                        ))
                        )}

                        {/* Regular Employees Section - Always show header */}
                        <div className="px-4 py-2 bg-slate-50 border-b border-t">
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            Employees ({regularEmployees.length})
                          </p>
                        </div>
                        {regularEmployees.length === 0 ? (
                          <div className="px-6 py-8 text-center text-slate-400">
                            No employees yet
                          </div>
                        ) : (
                          regularEmployees.map((employee) => (
                          <div 
                            key={employee.id} 
                            className="flex flex-col sm:flex-row sm:items-center justify-between px-3 sm:px-6 py-3 sm:py-4 hover:bg-slate-50 transition-colors gap-2 sm:gap-4"
                            data-testid={`employee-row-${employee.id}`}
                          >
                            <div className="flex items-center gap-3 sm:gap-4">
                              <div className="employee-avatar shrink-0">
                                {employee.name.charAt(0).toUpperCase()}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                                  <p className="font-medium text-sm sm:text-base truncate">{employee.name}</p>
                                </div>
                                <p className="text-xs sm:text-sm text-slate-500 truncate">{employee.email}</p>
                                <div className="flex items-center gap-2 sm:gap-4 text-xs text-slate-400 mt-1 flex-wrap">
                                  {employee.position && (
                                    <span className="flex items-center gap-1">
                                      <Briefcase className="w-2.5 h-2.5" />
                                      {employee.position}
                                    </span>
                                  )}
                                  {employee.allow_offsite_clock && (
                                    <span className="flex items-center gap-1 text-green-600">
                                      <MapPin className="w-2.5 h-2.5" />
                                      Off-site
                                    </span>
                                  )}
                                  <span className="hidden sm:flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    Joined {format(new Date(employee.created_at), 'dd/MM/yyyy')}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 sm:gap-2 flex-wrap self-end sm:self-auto">
                              {/* Roster Toggle */}
                              <Button
                                variant="outline"
                                size="sm"
                                className={`text-xs sm:text-sm ${employee.on_roster !== false
                                  ? 'text-blue-600 border-blue-200 bg-blue-50 hover:bg-blue-100' 
                                  : 'text-slate-400 border-slate-200 hover:text-slate-600'}`}
                                onClick={() => handleToggleRoster(employee)}
                                disabled={togglingRoster === employee.id}
                                data-testid={`roster-toggle-manager-${employee.id}`}
                              >
                                <Calendar className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                                <span className="hidden sm:inline">{employee.on_roster !== false ? 'On Roster' : 'Off Roster'}</span>
                              </Button>
                              {/* Off-site Toggle with Config */}
                              <Button
                                variant="outline"
                                size="sm"
                                className={`text-xs sm:text-sm ${employee.allow_offsite_clock 
                                  ? 'text-green-600 border-green-200 bg-green-50 hover:bg-green-100' 
                                  : 'text-slate-500 hover:text-slate-700'}`}
                                onClick={() => openOffsiteDialog(employee)}
                                data-testid={`offsite-config-${employee.id}`}
                              >
                                <MapPin className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                                <span className="hidden sm:inline">{employee.allow_offsite_clock 
                                  ? `Off-site` 
                                  : 'Off-site'}</span>
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs sm:text-sm text-slate-600 hover:text-blue-600 hover:border-blue-200"
                                onClick={() => openEditDialog(employee)}
                                data-testid={`edit-employee-${employee.id}`}
                              >
                                <Pencil className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                                <span className="hidden sm:inline">Edit</span>
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs sm:text-sm text-slate-600 hover:text-indigo-600 hover:border-indigo-200"
                                onClick={() => toggleManager(employee)}
                                data-testid={`toggle-manager-${employee.id}`}
                                title="Make Manager"
                              >
                                <Shield className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                                <span className="hidden sm:inline">Make Manager</span>
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs sm:text-sm text-amber-600 hover:text-amber-700 hover:border-amber-300 hover:bg-amber-50"
                                onClick={() => {
                                  setSelectedItem(employee);
                                  setArchiveDialogOpen(true);
                                }}
                                data-testid={`archive-employee-${employee.id}`}
                              >
                                <Archive className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                                <span className="hidden sm:inline">Archive</span>
                              </Button>
                            </div>
                          </div>
                        ))
                        )}
                        {/* Spacer for floating chat button */}
                        <div className="h-20 md:h-0"></div>
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Archived Tab */}
            <TabsContent value="archived">
              <Card className="border-0 shadow-lg">
                <CardHeader className="border-b">
                  <CardTitle className="font-['Manrope'] flex items-center gap-2">
                    <Archive className="w-5 h-5" />
                    Archived Employees
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {loading ? (
                    <div className="flex justify-center py-12">
                      <div className="spinner" />
                    </div>
                  ) : archivedEmployees.length === 0 ? (
                    <div className="empty-state py-12">
                      <Archive className="empty-state-icon" />
                      <p className="text-slate-500">No archived employees</p>
                      <p className="text-sm text-slate-400">Archived employees will appear here</p>
                    </div>
                  ) : (
                    <ScrollArea className="h-[400px]">
                      <div className="divide-y">
                        {archivedEmployees.map((employee) => (
                          <div 
                            key={employee.id} 
                            className="flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors bg-slate-100/50"
                            data-testid={`archived-employee-row-${employee.id}`}
                          >
                            <div className="flex items-center gap-4">
                              <div className="employee-avatar bg-slate-400">
                                {employee.name.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-slate-600">{employee.name}</p>
                                  <Badge variant="secondary" className="text-[10px]">
                                    <UserX className="w-3 h-3 mr-1" />
                                    Archived
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-4 text-sm text-slate-500">
                                  <span className="flex items-center gap-1">
                                    <Mail className="w-3 h-3" />
                                    {employee.email}
                                  </span>
                                  {employee.archived_at && (
                                    <span className="flex items-center gap-1">
                                      <Archive className="w-3 h-3" />
                                      Archived {format(new Date(employee.archived_at), 'dd/MM/yyyy')}
                                    </span>
                                  )}
                                </div>
                                {employee.payroll_id && (
                                  <div className="flex items-center gap-2 mt-1">
                                    <CreditCard className="w-3 h-3 text-slate-400" />
                                    <span className="text-xs text-slate-400">{employee.payroll_id}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-green-600 hover:text-green-700 hover:border-green-300 hover:bg-green-50"
                                onClick={() => handleRestore(employee)}
                                data-testid={`restore-employee-${employee.id}`}
                              >
                                <ArchiveRestore className="w-4 h-4 mr-1" />
                                Restore
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:border-red-300 hover:bg-red-50"
                                onClick={() => {
                                  setSelectedItem(employee);
                                  setHardDeleteDialogOpen(true);
                                }}
                                data-testid={`hard-delete-employee-${employee.id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete Permanently
                              </Button>
                            </div>
                          </div>
                        ))}
                        {/* Spacer for floating chat button */}
                        <div className="h-20 md:h-0"></div>
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Invites Tab */}
            <TabsContent value="invites">
              <Card className="border-0 shadow-lg">
                <CardHeader className="border-b">
                  <CardTitle className="font-['Manrope'] flex items-center gap-2">
                    <Link2 className="w-5 h-5" />
                    Pending Invitations
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {loading ? (
                    <div className="flex justify-center py-12">
                      <div className="spinner" />
                    </div>
                  ) : pendingInvites.length === 0 ? (
                    <div className="empty-state py-12">
                      <Link2 className="empty-state-icon" />
                      <p className="text-slate-500">No pending invites</p>
                      <p className="text-sm text-slate-400">All invitations have been accepted</p>
                    </div>
                  ) : (
                    <ScrollArea className="h-[400px]">
                      <div className="divide-y">
                        {pendingInvites.map((invite) => (
                          <div 
                            key={invite.id} 
                            className="px-6 py-4 hover:bg-slate-50 transition-colors"
                            data-testid={`invite-row-${invite.id}`}
                          >
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-semibold">
                                  {invite.name.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <p className="font-medium">{invite.name}</p>
                                  <p className="text-sm text-slate-500">{invite.email}</p>
                                </div>
                              </div>
                              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                                <ClockIcon className="w-3 h-3 mr-1" />
                                Pending
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 bg-slate-50 rounded-lg p-3">
                              <Input
                                value={invite.invite_link}
                                readOnly
                                className="text-xs bg-white"
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                className="shrink-0"
                                onClick={() => copyInviteLink(invite)}
                                data-testid={`copy-link-${invite.id}`}
                              >
                                {copiedLink === invite.id ? (
                                  <CheckCircle className="w-4 h-4 text-[hsl(var(--success))]" />
                                ) : (
                                  <Copy className="w-4 h-4" />
                                )}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="shrink-0 text-blue-600 border-blue-200 hover:bg-blue-50"
                                onClick={() => resendInviteEmail(invite)}
                                disabled={resendingInvite === invite.id}
                                data-testid={`resend-invite-${invite.id}`}
                              >
                                <RefreshCw className={`w-4 h-4 ${resendingInvite === invite.id ? 'animate-spin' : ''}`} />
                                <span className="ml-1 hidden sm:inline">Resend</span>
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="shrink-0 text-slate-400 hover:text-[hsl(var(--destructive))]"
                                onClick={() => {
                                  setSelectedItem(invite);
                                  setDeleteType('invite');
                                  setDeleteDialogOpen(true);
                                }}
                                data-testid={`cancel-invite-${invite.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                            <p className="text-xs text-slate-400 mt-2">
                              Created {format(new Date(invite.created_at), 'dd/MM/yyyy HH:mm')}
                            </p>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Cancel Invitation
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel the invitation for {selectedItem?.name}? They will no longer be able to register using this link.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/90"
              onClick={handleDelete}
              disabled={isSubmitting}
              data-testid="confirm-delete"
            >
              {isSubmitting ? <div className="spinner" /> : 'Cancel Invite'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive Employee Dialog */}
      <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Archive className="w-5 h-5 text-amber-600" />
              Archive Employee
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Are you sure you want to archive <strong>{selectedItem?.name}</strong>?
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <p className="font-medium mb-1">What happens when you archive:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-700">
                  <li>Employee will no longer appear in active lists</li>
                  <li>All time records are preserved for compliance</li>
                  <li>Employee cannot log in until restored</li>
                  <li>You can restore them at any time</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-amber-600 hover:bg-amber-700"
              onClick={handleArchive}
              disabled={isSubmitting}
              data-testid="confirm-archive"
            >
              {isSubmitting ? <div className="spinner" /> : 'Archive Employee'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Hard Delete Employee Dialog */}
      <AlertDialog open={hardDeleteDialogOpen} onOpenChange={setHardDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Permanently Delete Employee
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Are you sure you want to <strong className="text-red-600">permanently delete</strong> <strong>{selectedItem?.name}</strong>?
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                <p className="font-bold mb-2 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  This action is IRREVERSIBLE!
                </p>
                <p className="font-medium mb-1">The following will be permanently deleted:</p>
                <ul className="list-disc list-inside space-y-1 text-red-700">
                  <li>User account and login credentials</li>
                  <li>All time entries (clock-ins/outs)</li>
                  <li>All break records</li>
                  <li>All roster entries and schedules</li>
                  <li>All shift swap requests</li>
                  <li>Face recognition data</li>
                  <li>All notifications</li>
                </ul>
              </div>
              <p className="text-sm text-slate-600">
                Consider using <strong>Archive</strong> instead if you need to preserve records for compliance or may need to restore this employee later.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={handleHardDelete}
              disabled={isSubmitting}
              data-testid="confirm-hard-delete"
            >
              {isSubmitting ? <div className="spinner" /> : 'Delete Permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Employee Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              <Pencil className="w-5 h-5" />
              Edit Employee
            </DialogTitle>
            <DialogDescription>
              Update employee information. Changes will be saved immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Full Name *</Label>
                <Input
                  id="edit-name"
                  placeholder="John Doe"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  data-testid="edit-employee-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email *</Label>
                <Input
                  id="edit-email"
                  type="email"
                  placeholder="john@company.com"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  data-testid="edit-employee-email-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-position">Position</Label>
                <Input
                  id="edit-position"
                  placeholder="e.g., Barista, Chef"
                  value={editForm.position}
                  onChange={(e) => setEditForm({ ...editForm, position: e.target.value })}
                  data-testid="edit-employee-position-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-payroll-id">Payroll ID</Label>
                <Input
                  id="edit-payroll-id"
                  placeholder="EMP001"
                  value={editForm.payroll_id}
                  onChange={(e) => setEditForm({ ...editForm, payroll_id: e.target.value })}
                  data-testid="edit-employee-payroll-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-dob">Date of Birth</Label>
              <Input
                id="edit-dob"
                type="date"
                value={editForm.date_of_birth}
                onChange={(e) => setEditForm({ ...editForm, date_of_birth: e.target.value })}
                data-testid="edit-employee-dob-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-address">Address</Label>
              <Textarea
                id="edit-address"
                placeholder="123 Main Street, City, Country"
                value={editForm.address}
                onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                rows={2}
                data-testid="edit-employee-address-input"
              />
            </div>
            {/* Scheduling role for AI Rostering Assistant */}
            <div className="border-t pt-3">
              <Label className="text-xs font-semibold text-indigo-700 uppercase">AI Scheduling</Label>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div className="space-y-2">
                  <Label htmlFor="edit-sched-role" className="text-sm">Role</Label>
                  <select
                    id="edit-sched-role"
                    value={editForm.scheduling_role || 'normal'}
                    onChange={(e) => setEditForm({ ...editForm, scheduling_role: e.target.value })}
                    className="w-full h-9 rounded-md border border-slate-300 px-2 text-sm"
                    data-testid="edit-employee-scheduling-role"
                  >
                    <option value="normal">Normal</option>
                    <option value="early_relief_only">Early-start relief only</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-permitted-times" className="text-sm">Permitted start times</Label>
                  <Input
                    id="edit-permitted-times"
                    placeholder="04:30, 05:00"
                    value={editForm.permitted_start_times || ''}
                    onChange={(e) => setEditForm({ ...editForm, permitted_start_times: e.target.value })}
                    data-testid="edit-employee-permitted-times"
                  />
                </div>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">
                Comma-separated HH:MM list. Leave blank to allow all times.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                setEditDialogOpen(false);
                setEditingEmployee(null);
              }}
              data-testid="edit-employee-cancel-btn"
            >
              <X className="w-4 h-4 mr-1" />
              Cancel
            </Button>
            <Button 
              className="bg-[hsl(243,65%,20%)] hover:bg-[hsl(243,65%,25%)]"
              onClick={handleEditSave}
              disabled={editSaving}
              data-testid="edit-employee-save-btn"
            >
              {editSaving ? (
                <div className="spinner" />
              ) : (
                <>
                  <Save className="w-4 h-4 mr-1" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Off-site Settings Dialog */}
      <Dialog open={offsiteDialogOpen} onOpenChange={setOffsiteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              <MapPin className="w-5 h-5 text-green-600" />
              Off-site Clock-in Settings
            </DialogTitle>
            <DialogDescription>
              Configure off-site clock-in permission for {selectedOffsiteEmployee?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <Label className="font-medium">Allow Off-site Clock-in</Label>
                  <p className="text-sm text-slate-500">
                    Enable clock-in from outside geofenced areas
                  </p>
                </div>
              </div>
              <Switch
                checked={offsiteSettings.allow_offsite_clock}
                onCheckedChange={(checked) => 
                  setOffsiteSettings(prev => ({ ...prev, allow_offsite_clock: checked }))
                }
                data-testid="offsite-enable-toggle"
              />
            </div>

            {/* Enforcement Mode */}
            {offsiteSettings.allow_offsite_clock && (
              <div className="space-y-3">
                <Label className="font-medium">Enforcement Mode</Label>
                <p className="text-sm text-slate-500">
                  What should happen when this employee clocks in outside the geofence?
                </p>
                <Select
                  value={offsiteSettings.offsite_enforcement_mode}
                  onValueChange={(value) => 
                    setOffsiteSettings(prev => ({ ...prev, offsite_enforcement_mode: value }))
                  }
                >
                  <SelectTrigger data-testid="offsite-enforcement-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="warn">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                        <span>Warn Only</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="block">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-red-500" />
                        <span>Allow (Previously Blocked)</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                
                <div className={`p-3 rounded-lg text-sm ${
                  offsiteSettings.offsite_enforcement_mode === 'warn' 
                    ? 'bg-amber-50 border border-amber-200 text-amber-800' 
                    : 'bg-green-50 border border-green-200 text-green-800'
                }`}>
                  {offsiteSettings.offsite_enforcement_mode === 'warn' ? (
                    <p className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>
                        <strong>Warn Mode:</strong> The employee will be allowed to clock in off-site, 
                        but a warning will be logged and managers will be notified.
                      </span>
                    </p>
                  ) : (
                    <p className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>
                        <strong>Allow Mode:</strong> The employee can clock in from any location 
                        without restrictions.
                      </span>
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                setOffsiteDialogOpen(false);
                setSelectedOffsiteEmployee(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              className="bg-[hsl(243,65%,20%)] hover:bg-[hsl(243,65%,25%)]"
              onClick={handleSaveOffsiteSettings}
              disabled={savingOffsite}
              data-testid="save-offsite-settings-btn"
            >
              {savingOffsite ? (
                <div className="spinner" />
              ) : (
                <>
                  <Save className="w-4 h-4 mr-1" />
                  Save Settings
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeManagement;
